import * as THREE from "three";
import { DECOR_DEPTH, DECOR_WIDTH, FLOOR_Y, TABLE_CENTER_Z } from "./table";
import { generateFlameTexture, generateStoneTexture, generateWoodTexture } from "./textures";

/**
 * Decor de la salle de taverne autour de la table : sol en planches, murs de
 * pierre, cheminee, tonneaux, etagere a bouteilles et bougies. Tout est
 * volontairement low-poly et sombre : l'arriere-plan donne de la profondeur
 * mais ne doit jamais voler la vedette a la table (cahier des charges §3),
 * le brouillard et l'eclairage s'en chargent.
 */

const ROOM_RADIUS = 14;
const WALL_HEIGHT = 7;

/** Une flamme animee : sprite additif + lumiere ponctuelle qui vacille. */
export interface Flame {
  sprite: THREE.Sprite;
  light: THREE.PointLight;
  baseIntensity: number;
  flickerSeed: number;
}

export interface Environment {
  group: THREE.Group;
  flames: Flame[];
}

export function buildEnvironment(): Environment {
  const group = new THREE.Group();
  const flames: Flame[] = [];
  const flameTex = generateFlameTexture();

  // --- Sol en larges planches sombres.
  const floorWood = generateWoodTexture({ seed: 11, baseColor: "#33210f", plankCount: 10 });
  floorWood.map.repeat.set(4, 4);
  floorWood.roughnessMap.repeat.set(4, 4);
  const floor = new THREE.Mesh(
    new THREE.CircleGeometry(ROOM_RADIUS, 32),
    new THREE.MeshStandardMaterial({ map: floorWood.map, roughnessMap: floorWood.roughnessMap, roughness: 1 }),
  );
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = FLOOR_Y;
  group.add(floor);

  // --- Mur circulaire de pierre (cylindre vu de l'interieur).
  const stone = generateStoneTexture({ seed: 5, baseColor: "#453f36" });
  stone.map.repeat.set(8, 2);
  stone.roughnessMap.repeat.set(8, 2);
  const wall = new THREE.Mesh(
    new THREE.CylinderGeometry(ROOM_RADIUS, ROOM_RADIUS, WALL_HEIGHT, 24, 1, true),
    new THREE.MeshStandardMaterial({
      map: stone.map,
      roughnessMap: stone.roughnessMap,
      roughness: 1,
      side: THREE.BackSide,
    }),
  );
  wall.position.y = FLOOR_Y + WALL_HEIGHT / 2;
  group.add(wall);

  // Pas de poutres de plafond : la camera, tres rapprochee et au champ de
  // vision tres large sur mobile (cf. render/scene.ts), peut faire passer un
  // rayon tout pres de leur hauteur meme loin de l'axe de vue — elles se
  // sont deja retrouvees a barrer l'ecran d'un bandeau de bois inattendu.
  // Sans compter que le feutre, desormais demesure (cf. render/table.ts),
  // masque de toute facon la salle : leur seul role visuel a disparu.
  const beamWood = generateWoodTexture({ seed: 14, baseColor: "#2e1d0e", plankCount: 3 });
  const beamMat = new THREE.MeshStandardMaterial({ map: beamWood.map, roughness: 1 });

  // --- Cheminee au fond (face au joueur, derriere l'adversaire).
  const hearth = buildHearth(beamMat);
  hearth.position.set(0, FLOOR_Y, -ROOM_RADIUS * 0.86);
  group.add(hearth);
  const hearthFlame = makeFlame(flameTex, 1.6, "#ff8a30", 2.4, 9);
  hearthFlame.sprite.position.set(0, FLOOR_Y + 0.9, -ROOM_RADIUS * 0.86 + 0.5);
  hearthFlame.light.position.copy(hearthFlame.sprite.position).add(new THREE.Vector3(0, 0.2, 0.6));
  group.add(hearthFlame.sprite, hearthFlame.light);
  flames.push(hearthFlame);

  // --- Tonneaux sur les cotes.
  const barrelWood = generateWoodTexture({ seed: 21, baseColor: "#4a2f1a", plankCount: 12 });
  const barrelMat = new THREE.MeshStandardMaterial({ map: barrelWood.map, roughness: 0.95 });
  const barrelPositions: [number, number, number][] = [
    [-ROOM_RADIUS * 0.62, 0, -ROOM_RADIUS * 0.5],
    [ROOM_RADIUS * 0.66, 0, -ROOM_RADIUS * 0.42],
    [ROOM_RADIUS * 0.7, 0, -ROOM_RADIUS * 0.28],
  ];
  for (const [x, , z] of barrelPositions) {
    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.55, 1.5, 12), barrelMat);
    barrel.position.set(x, FLOOR_Y + 0.75, z);
    group.add(barrel);
  }

  // --- Etagere a bouteilles sur le mur du fond (comptoir du tavernier).
  const shelf = buildShelf(beamMat);
  shelf.position.set(-ROOM_RADIUS * 0.55, FLOOR_Y + 1.4, -ROOM_RADIUS * 0.78);
  shelf.lookAt(new THREE.Vector3(0, FLOOR_Y + 1.4, 0));
  group.add(shelf);

  // Pas de chandeliers sur le feutre : demandes hors du champ de vision — la
  // "lumiere centrale" vient desormais uniquement du point lumineux de la
  // scene (render/scene.ts) et de la vignette d'ecran (cf. src/style.css).

  // --- La chope du Taverneu, posee au bord de la table, cote joueur (cahier
  // des charges §3 : decor, a portee de main mais hors du tapis de jeu).
  const tankard = buildTankard();
  tankard.position.set(-DECOR_WIDTH / 2 + 0.2, 0, TABLE_CENTER_Z + DECOR_DEPTH / 2 - 1.6);
  group.add(tankard);

  return { group, flames };
}

/** Chope de biere en bois cercle de fer, avec anse et mousse debordante. */
function buildTankard(): THREE.Group {
  const tankard = new THREE.Group();
  const woodMat = new THREE.MeshStandardMaterial({ color: "#4a2f1a", roughness: 0.85 });
  const ironMat = new THREE.MeshStandardMaterial({ color: "#3a3025", roughness: 0.4, metalness: 0.6 });
  const foamMat = new THREE.MeshStandardMaterial({ color: "#f0e6c8", roughness: 0.7 });

  const body = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.19, 0.4, 12), woodMat);
  body.position.y = 0.2;
  body.castShadow = true;
  tankard.add(body);

  for (const y of [0.08, 0.34]) {
    const band = new THREE.Mesh(new THREE.TorusGeometry(y < 0.2 ? 0.185 : 0.165, 0.012, 6, 16), ironMat);
    band.rotation.x = Math.PI / 2;
    band.position.y = y;
    tankard.add(band);
  }

  const handle = new THREE.Mesh(new THREE.TorusGeometry(0.1, 0.02, 6, 12, Math.PI), woodMat);
  handle.position.set(0.19, 0.2, 0);
  handle.rotation.z = -Math.PI / 2;
  tankard.add(handle);

  // Mousse : petit dome + une coulure figee sur le flanc.
  const foam = new THREE.Mesh(new THREE.SphereGeometry(0.155, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2), foamMat);
  foam.position.y = 0.4;
  foam.scale.y = 0.45;
  tankard.add(foam);
  const drip = new THREE.Mesh(new THREE.CapsuleGeometry(0.02, 0.06, 3, 6), foamMat);
  drip.position.set(-0.15, 0.36, 0.04);
  tankard.add(drip);

  return tankard;
}

/** Fait vaciller les flammes (a appeler a chaque frame). */
export function updateFlames(flames: Flame[], time: number): void {
  for (const flame of flames) {
    const s = flame.flickerSeed;
    const flicker =
      0.82 +
      0.13 * Math.sin(time * 9.3 + s) +
      0.05 * Math.sin(time * 23.7 + s * 2.1) +
      0.04 * Math.sin(time * 5.1 + s * 0.7);
    flame.light.intensity = flame.baseIntensity * flicker;
    flame.sprite.scale.setScalar(flame.sprite.userData.baseScale * (0.92 + flicker * 0.1));
  }
}

function makeFlame(
  texture: THREE.Texture,
  scale: number,
  color: string,
  intensity: number,
  seed: number,
): Flame {
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: texture,
      color,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
    }),
  );
  sprite.scale.setScalar(scale);
  sprite.userData.baseScale = scale;
  const light = new THREE.PointLight(color, intensity, 0, 1.9);
  return { sprite, light, baseIntensity: intensity, flickerSeed: seed };
}

/** Cheminee : deux jambages + linteau de pierre, atre noir. */
function buildHearth(woodMat: THREE.MeshStandardMaterial): THREE.Group {
  const hearth = new THREE.Group();
  const stone = generateStoneTexture({ seed: 8, baseColor: "#3a352d" });
  const stoneMat = new THREE.MeshStandardMaterial({ map: stone.map, roughness: 1 });

  for (const side of [-1, 1]) {
    const jamb = new THREE.Mesh(new THREE.BoxGeometry(0.7, 2.4, 0.9), stoneMat);
    jamb.position.set(side * 1.45, 1.2, 0);
    hearth.add(jamb);
  }
  const lintel = new THREE.Mesh(new THREE.BoxGeometry(3.8, 0.6, 1), stoneMat);
  lintel.position.set(0, 2.6, 0);
  hearth.add(lintel);

  // Fond noir de l'atre : la flamme (sprite additif) s'y detache bien.
  const back = new THREE.Mesh(
    new THREE.BoxGeometry(2.2, 2.4, 0.2),
    new THREE.MeshStandardMaterial({ color: "#0a0604", roughness: 1 }),
  );
  back.position.set(0, 1.2, -0.3);
  hearth.add(back);

  // Manteau en bois au-dessus.
  const mantel = new THREE.Mesh(new THREE.BoxGeometry(4.2, 0.18, 1.1), woodMat);
  mantel.position.set(0, 2.98, 0);
  hearth.add(mantel);

  return hearth;
}

/** Etagere murale garnie de bouteilles colorees. */
function buildShelf(woodMat: THREE.MeshStandardMaterial): THREE.Group {
  const shelf = new THREE.Group();
  const bottleColors = ["#3a5e2f", "#5e2f2f", "#8a6a2a", "#2f4a5e", "#4a2f5e"];
  for (let level = 0; level < 2; level++) {
    const board = new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.1, 0.45), woodMat);
    board.position.y = level * 0.85;
    shelf.add(board);
    for (let i = 0; i < 5; i++) {
      const color = bottleColors[(i + level * 2) % bottleColors.length]!;
      const bottle = new THREE.Mesh(
        new THREE.CylinderGeometry(0.07, 0.09, 0.42, 8),
        new THREE.MeshStandardMaterial({ color, roughness: 0.25, metalness: 0.1 }),
      );
      bottle.position.set(-1.05 + i * 0.5, level * 0.85 + 0.27, 0);
      shelf.add(bottle);
    }
  }
  return shelf;
}
